import React, { CSSProperties, useEffect, useMemo, useState } from 'react';
import Slider from 'react-slick';
import Link from 'next/link';
import { getHeapAttributes } from '../../../utils/heap/heap.helper';
import ImageComponent from '../../atoms/image-component/image-component';
import {
  ISidebarNavigation,
  NavLinksFields,
} from '../../../models/sidebar-navigation/ISidebarNavigation';
import useWindowResize from '../../../hooks/use-window-resize.hook';
import { deviceDetect } from '../../../data/device-detect';
import breakpoints from '../../../styles/partials/breakpoints.module.scss';
import styles from './sidebar-navigation.module.scss';
import LoadingSpinner from '../../atoms/loading-spinner/loading-spinner';

type Props = {
  sidebarNavigationProps: ISidebarNavigation;
  sidebarBrandColor: string;
  b2cFlag: boolean;
  pagePath?: string;
  locales?: string[];
};

type SidebarNavLinkItemProps = {
  link: NavLinksFields;
  sidebarBrandColor: string;
  b2cFlag: boolean;
  pagePath?: string;
  locales?: string[];
};

const SidebarNavLinkItem: React.FC<SidebarNavLinkItemProps> = ({
  link,
  sidebarBrandColor,
  b2cFlag,
  pagePath,
  locales,
}) => {
  const isVisible = React.useMemo(
    () =>
      link.application === 'Both' ||
      (link.application === 'PartnerPortal' && !b2cFlag) ||
      (link.application === 'IntoStudy' && b2cFlag),
    [b2cFlag, link],
  );

  const renderData = useMemo(() => {
    let localeValue = '';

    if (locales) {
      localeValue = locales.find((locale) => link.urlLink?.includes(`/${locale}/`));
    }

    const classNames = [styles['sidebar-nav-link']];

    if (pagePath && (localeValue ? `/${localeValue}${pagePath}` : pagePath) === link.urlLink) {
      classNames.push(styles[`active`]);
    }

    const className = classNames.join(' ');

    const sidebarNavLinkStyle = {
      '--navigation-active-color': sidebarBrandColor,
      '--navigation-active-color-alpha': sidebarBrandColor + '4f',
    } as CSSProperties;

    const heapAttributes = getHeapAttributes(`sidebar-navigation`, {
      linkName: link.displayName,
    });

    return {
      sidebarNavLinkStyle,
      className,
      heapAttributes,
    };
  }, [link, locales, pagePath, sidebarBrandColor]);

  if (!isVisible) {
    return null;
  }

  return (
    <span className={renderData.className} style={renderData.sidebarNavLinkStyle}>
      <Link
        href={link.urlLink}
        aria-label={`link to ${link.displayName}`}
        {...renderData.heapAttributes}
      >
        {link.displayName}
      </Link>
    </span>
  );
};

export default function SidebarNavigation({
  sidebarNavigationProps,
  sidebarBrandColor,
  b2cFlag,
  pagePath,
  locales,
}: Props) {
  const [isBreakpointTabletUp, setIsBreakpointTabletUp] = useState(!deviceDetect.isMobile);

  useWindowResize(
    (newWidth) => setIsBreakpointTabletUp(newWidth >= parseInt(breakpoints.tablet_landscape, 10)),
    100,
  );

  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    setLoading(false);
  }, []);

  const renderData = React.useMemo(() => {
    const links = (sidebarNavigationProps?.links ? sidebarNavigationProps?.links : [])
      .filter((x) => x.fields)
      .map((x) => x.fields);

    const hasTopImage = !!sidebarNavigationProps?.topImage?.fields?.image.length;

    return { links, hasTopImage };
  }, []);

  if (loading) {
    return <LoadingSpinner src={'/public/next-assets/loaders/into.svg'} />;
  }

  if (isBreakpointTabletUp) {
    return (
      <aside className={`${styles[`sidebar-navigation`]} py-lg-4 py-0`}>
        <div className={`${styles[`sidebar-sticky`]}`}>
          {renderData.hasTopImage && (
            <div className={`${styles[`sidebar-image`]} text-left`}>
              <ImageComponent
                src={sidebarNavigationProps.topImage?.fields?.image[0].secure_url}
                altText={sidebarNavigationProps.topImage?.fields?.altText}
                width={210}
                height={60}
                layout='fixed'
                objectFit='contain'
              />
            </div>
          )}
          {renderData.links.length && (
            <ul className={`${styles[`sidebar-nav`]} ${renderData.hasTopImage ? 'pt-3' : null}`}>
              {renderData.links.map((link: NavLinksFields) => (
                <li key={link.displayName} className={`${styles[`sidebar-nav-item`]}`}>
                  <SidebarNavLinkItem
                    link={link}
                    sidebarBrandColor={sidebarBrandColor}
                    b2cFlag={b2cFlag}
                    pagePath={pagePath}
                    locales={locales}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>
    );
  }

  return (
    <section
      className={`${styles[`sidebar-nav-carousel`]} nav-item-carousel ${
        renderData.hasTopImage ? null : 'mb-3'
      }`}
    >
      {!!renderData.links.length && (
        <Slider
          slidesToShow={1}
          slidesToScroll={1}
          dots={false}
          arrows={true}
          infinite={false}
          variableWidth={true}
          outerEdgeLimit={true}
        >
          {renderData.links.map((link: NavLinksFields, index) => (
            <div key={link.displayName} className={`${styles[`sidebar-nav-item`]}`}>
              <SidebarNavLinkItem
                link={link}
                sidebarBrandColor={sidebarBrandColor}
                b2cFlag={b2cFlag}
                pagePath={pagePath}
                locales={locales}
              />
            </div>
          ))}
        </Slider>
      )}
      {renderData.hasTopImage && (
        <div className={`${styles[`sidebar-image`]} text-center my-3`}>
          <ImageComponent
            src={sidebarNavigationProps.topImage?.fields?.image[0].secure_url}
            altText={sidebarNavigationProps.topImage?.fields?.altText}
            width={210}
            height={60}
            layout='fixed'
            objectFit='contain'
          />
        </div>
      )}
    </section>
  );
}

//SidebarNavigation.whyDidYouRender = true;
